/**
 * Import as matches — a lead link's uploads become a club's games.
 *
 * Platform admins only (the server refuses everyone else with a 404).
 * The server proposes one match per vendor delivery (files sharing a
 * leading token, g2645198_…), filled from a Second Spectrum metadata JSON
 * where there is one; anything else is typed here. Importing a match is
 * one request (the games, game_files and game_facts rows), then copy
 * requests for one file at a time into the game's input/ folder. Each
 * copies for a bounded time; a big file answers "partial" and is asked
 * for again until it lands — the server resumes the same multipart
 * upload, and a file already copied is a no-op.
 *
 * The copies run in THIS tab: closing or reloading it stops them (an
 * import of the match again resumes them), so while any is running the
 * page says so, asks before the tab closes, and asks before a link inside
 * the app takes you away. The links onward (Matches, Layers) appear only
 * once every file has landed.
 *
 * After it: set the video times on the Layers page, Import there, build.
 */
import { Download } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'

import { ApiError, api } from '../lib/api'

type LeadFile = {
  key: string
  slot: string
  filename: string
  size_bytes: number | null
}
type Proposal = {
  away: string | null
  home: string | null
  game_code: string | null
  game_date: string | null
  fps: number | null
  total_frames: number | null
  half2_frame: number | null
  home_attack_dir_h1: 'left' | 'right' | null
}
type PreviewMatch = {
  token: string | null
  files: LeadFile[]
  metadata_key: string | null
  proposal: Proposal
  existing_game: { id: number; game_code: string } | null
  warnings: string[]
}
type Club = { id: number; code: string; name: string }
type CopyState = { status: 'waiting' | 'copying' | 'copied' | 'skipped' | 'failed'
                   detail?: string }
type CopyReply = { status: 'copied' | 'skipped' | 'partial'
                   size_bytes: number; copied_bytes: number
                   conflicts: string[] }
type ImportResult = {
  game_id: number
  game_code: string
  created: boolean
  conflicts: string[]
  copies: { key: string; filename: string; slot: string; dest_key: string }[]
}

const msgOf = (e: unknown) => (e instanceof ApiError ? e.message : String(e))

export default function LeadImport({ linkId }: { linkId: number }) {
  const [open, setOpen] = useState(false)
  const [clubs, setClubs] = useState<Club[] | null>(null)
  const [clubId, setClubId] = useState('')
  const [matches, setMatches] = useState<PreviewMatch[] | null>(null)
  const [truncated, setTruncated] = useState(false)
  const [reason, setReason] = useState('')
  const [error, setError] = useState<string | null>(null)
  // Rows with copies still running, by match token.
  const [copying, setCopying] = useState<Set<string>>(() => new Set())
  const onCopying = useCallback((row: string, on: boolean) => {
    setCopying((s) => {
      if (s.has(row) === on) return s
      const n = new Set(s)
      if (on) n.add(row)
      else n.delete(row)
      return n
    })
  }, [])
  useLeaveGuard(copying.size > 0)

  useEffect(() => {
    if (!open || clubs) return
    api<{ organizations: Club[] }>('/api/v1/admin/orgs')
      .then((d) => setClubs(d.organizations))
      .catch((e: unknown) => setError(msgOf(e)))
  }, [open, clubs])

  useEffect(() => {
    if (!open) return
    setMatches(null)
    const q = clubId ? `?club_id=${clubId}` : ''
    api<{ matches: PreviewMatch[]; truncated: boolean }>(
      `/api/v1/admin/lead-links/${linkId}/import-preview${q}`)
      .then((d) => { setMatches(d.matches); setTruncated(d.truncated) })
      .catch((e: unknown) => setError(msgOf(e)))
  }, [open, clubId, linkId])

  return (
    <div>
      <button type="button" className="pg-link t-data"
              style={{ color: 'var(--color-text-faint)' }}
              onClick={() => setOpen(!open)}>
        {open ? 'close import' : 'import as matches…'}
      </button>
      {open && (
        <div className="panel" style={{ marginTop: 'var(--space-2)' }}>
          {error && <p className="adm-msg adm-msg--err">{error}</p>}
          <div className="adm-bar">
            <label className="adm-field">
              <span className="t-label">Club</span>
              <select className="adm-select" value={clubId}
                      aria-label="Club"
                      onChange={(e) => setClubId(e.target.value)}>
                <option value="">Choose a club…</option>
                {(clubs ?? []).map((c) => (
                  <option key={c.id} value={c.id}>{c.name} ({c.code})</option>
                ))}
              </select>
            </label>
            <label className="adm-field" style={{ flex: 1 }}>
              <span className="t-label">Reason (kept in the audit log)</span>
              <input className="adm-input" value={reason}
                     aria-label="Reason"
                     placeholder="e.g. Leeds onboarding, Sept fixtures"
                     onChange={(e) => setReason(e.target.value)} />
            </label>
          </div>
          {matches === null && !error && <p className="muted">Loading…</p>}
          {truncated && (
            <p className="adm-msg adm-msg--err">
              This link holds more files than one listing returns — some
              matches are missing below. Import what is shown, or move
              files out of the link&rsquo;s folder.
            </p>
          )}
          {matches?.length === 0 && (
            <p className="muted">No files in storage for this link.</p>
          )}
          {matches?.map((m) => (
            <MatchRow key={m.token ?? '(other)'} linkId={linkId} match={m}
                      clubId={clubId} reason={reason}
                      rowId={m.token ?? '(other)'} onCopying={onCopying} />
          ))}
        </div>
      )}
    </div>
  )
}

function MatchRow({ linkId, match, clubId, reason, rowId, onCopying }: {
  linkId: number; match: PreviewMatch; clubId: string; reason: string
  rowId: string
  /** (row, true) while this row's copies are running. Must be stable:
   *  the effects below depend on it. */
  onCopying: (row: string, on: boolean) => void
}) {
  const p = match.proposal
  const [code, setCode] = useState(p.game_code ?? '')
  const [gameDate, setGameDate] = useState(p.game_date ?? '')
  const [fps, setFps] = useState(p.fps != null ? String(p.fps) : '')
  const [frames, setFrames] = useState(
    p.total_frames != null ? String(p.total_frames) : '')
  const [half2, setHalf2] = useState(
    p.half2_frame != null ? String(p.half2_frame) : '')
  const [dir, setDir] = useState<string>(p.home_attack_dir_h1 ?? '')
  const [keys, setKeys] = useState<Set<string>>(
    () => new Set(match.files.map((f) => f.key)))
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState<ImportResult | null>(null)
  const [copies, setCopies] = useState<Record<string, CopyState>>({})
  const [error, setError] = useState<string | null>(null)

  const states = Object.values(copies)
  const total = states.length
  const landed = states.filter((c) => c.status === 'copied' || c.status === 'skipped').length
  const failed = states.filter((c) => c.status === 'failed').length
  const running = total > 0 && landed + failed < total
  const allLanded = total > 0 && landed === total
  useEffect(() => { onCopying(rowId, running) }, [rowId, running, onCopying])
  // Stop guarding if the row goes away mid-copy (the club changes).
  useEffect(() => () => onCopying(rowId, false), [rowId, onCopying])

  // The match facts are required once per match, server-side: a group
  // that only ADDS FILES to a match already imported (the video after
  // the tracking pair) needs just the game code — re-typing facts here
  // fed values the server was discarding anyway (it never overwrites).
  // A number typed must still be a number.
  const numOk = (v: string) => !v || Number(v) > 0
  const ready = clubId && reason.trim() && code.trim() && keys.size > 0
    && numOk(fps) && numOk(frames)

  const copyOne = async (gameId: number, key: string) => {
    setCopies((c) => ({ ...c, [key]: { status: 'copying' } }))
    try {
      // "partial": the server ran out of its time budget and resumes
      // from where it stopped on the next request. Three answers in a row
      // with no new bytes means it is not getting anywhere: stop, offer
      // retry, rather than loop forever.
      let last = -1, stalled = 0
      for (;;) {
        const r = await api<CopyReply>(
          `/api/v1/admin/lead-links/${linkId}/import/copy`,
          { method: 'POST', body: JSON.stringify({ game_id: gameId, key }) })
        if (r.status === 'partial') {
          stalled = r.copied_bytes > last ? 0 : stalled + 1
          last = r.copied_bytes
          if (stalled >= 3) {
            setCopies((c) => ({ ...c, [key]: { status: 'failed',
              detail: 'the copy stopped making progress' } }))
            return
          }
          const pct = Math.floor((100 * r.copied_bytes) / (r.size_bytes || 1))
          setCopies((c) => ({ ...c, [key]: { status: 'copying',
                                             detail: `${pct}%` } }))
          continue
        }
        const status = r.status
        setCopies((c) => ({ ...c, [key]: {
          status, detail: r.conflicts.join('; ') || undefined } }))
        return
      }
    } catch (e) {
      setCopies((c) => ({ ...c, [key]: { status: 'failed', detail: msgOf(e) } }))
    }
  }

  const run = async () => {
    setBusy(true); setError(null)
    try {
      const r = await api<ImportResult>(
        `/api/v1/admin/lead-links/${linkId}/import`,
        { method: 'POST', body: JSON.stringify({
          club_id: Number(clubId), game_code: code.trim(),
          game_date: gameDate || null, fps: fps ? Number(fps) : null,
          total_frames: frames ? Number(frames) : null,
          half2_frame: half2 ? Number(half2) : null,
          home_attack_dir_h1: dir || null, keys: [...keys],
          metadata_key: match.metadata_key, reason: reason.trim(),
        }) })
      setResult(r)
      setCopies(Object.fromEntries(
        r.copies.map((c) => [c.key, { status: 'waiting' } as CopyState])))
      // One file at a time: each is its own request, and R2 already runs
      // several parts of it at once.
      for (const c of r.copies) await copyOne(r.game_id, c.key)
    } catch (e) {
      setError(msgOf(e))
    } finally { setBusy(false) }
  }

  const field = (label: string, value: string, set: (v: string) => void,
                 width = 110) => (
    <label className="adm-field">
      <span className="t-label">{label}</span>
      <input className="adm-input" style={{ width }} value={value}
             aria-label={label} disabled={!!result}
             onChange={(e) => set(e.target.value)} />
    </label>
  )

  return (
    <div style={{ borderTop: '1px solid var(--color-border)',
                  padding: 'var(--space-3) 0' }}>
      <div className="t-data" style={{ marginBottom: 'var(--space-2)' }}>
        <strong>{match.token ?? 'Other files'}</strong>
        {match.existing_game && (
          <span className="muted"> · this club already has
            {' '}{match.existing_game.game_code} — importing fills only
            what it is missing</span>
        )}
      </div>
      {match.warnings.map((w) => (
        <div key={w} className="t-data" style={{ color: 'var(--state-watch, #c90)' }}>
          {w}
        </div>
      ))}
      <div className="t-data" style={{ color: 'var(--color-text-faint)' }}>
        The facts below are needed once per match — adding files to a
        match already imported takes just its game code; what is on file
        is kept.
      </div>
      <div className="adm-bar">
        {field('Game code (AWAY@HOME)', code, setCode, 190)}
        <label className="adm-field">
          <span className="t-label">Date</span>
          <input className="adm-input" type="date" value={gameDate}
                 aria-label="Date" disabled={!!result}
                 onChange={(e) => setGameDate(e.target.value)} />
        </label>
        {field('FPS', fps, setFps, 60)}
        {field('Total frames', frames, setFrames)}
        {field('2nd half frame', half2, setHalf2)}
        <label className="adm-field">
          <span className="t-label">Home attacks (1st half)</span>
          <select className="adm-select" value={dir} disabled={!!result}
                  aria-label="Home attacks (1st half)"
                  onChange={(e) => setDir(e.target.value)}>
            <option value="">—</option>
            <option value="right">right</option>
            <option value="left">left</option>
          </select>
        </label>
      </div>
      <div style={{ margin: 'var(--space-2) 0' }}>
        {match.files.map((f) => {
          const c = copies[f.key]
          return (
            <label key={f.key} className="t-data" style={{ display: 'block' }}>
              <input type="checkbox" checked={keys.has(f.key)}
                     disabled={!!result}
                     onChange={(e) => setKeys((s) => {
                       const n = new Set(s)
                       if (e.target.checked) n.add(f.key)
                       else n.delete(f.key)
                       return n
                     })} />{' '}
              {f.filename}{' '}
              <span style={{ color: 'var(--color-text-faint)' }}>
                {f.slot}
              </span>
              {c && (
                <span style={{ marginLeft: 8,
                  color: c.status === 'failed' ? 'var(--state-declining)'
                    : c.status === 'copied' || c.status === 'skipped'
                      ? 'var(--state-improving)' : 'var(--color-text-muted)' }}>
                  {c.status === 'skipped' ? 'already there' : c.status}
                  {c.detail ? ` — ${c.detail}` : ''}
                </span>
              )}
              {c?.status === 'failed' && result && (
                <button type="button" className="pg-link" style={{ marginLeft: 6 }}
                        onClick={() => void copyOne(result.game_id, f.key)}>
                  retry
                </button>
              )}
            </label>
          )
        })}
      </div>
      {!result && (
        <button className="adm-btn" disabled={!ready || busy}
                onClick={() => void run()}>
          <Download size={13} />
          {busy ? 'Importing…' : 'Import this match'}
        </button>
      )}
      {error && <p className="adm-msg adm-msg--err">{error}</p>}
      {result && (
        <div className="t-data" style={{ marginTop: 'var(--space-2)' }}>
          {result.created ? 'Created' : 'Updated'} game {result.game_id}
          {' '}({result.game_code}).
          {running && (
            <p className="adm-msg adm-msg--err" role="status">
              Copying file {Math.min(landed + failed + 1, total)} of {total}
              {' '}— keep this page open until it finishes. Closing or
              reloading the tab stops the copy; importing this match again
              picks up where it stopped.
            </p>
          )}
          {!running && failed > 0 && (
            <p className="adm-msg adm-msg--err" role="status">
              {landed} of {total} files copied; {failed} failed — retry
              {failed === 1 ? ' it' : ' them'} above before moving on.
            </p>
          )}
          {allLanded && (
            <p className="adm-msg adm-msg--ok" role="status">
              All {total} {total === 1 ? 'file' : 'files'} copied.{' '}
              <a href="/match-room">Match Room</a>{' · '}
              <a href={`/ops/layer-stack?view=match&game=${result.game_id}`
                       + `&club=${clubId}`}>
                Layers — set video times, then Import
              </a>
            </p>
          )}
          {result.conflicts.map((c) => (
            <div key={c} className="muted">kept: {c}</div>
          ))}
        </div>
      )}
    </div>
  )
}

export const LEAVE_WARNING = 'Files are still copying. Leaving this page '
  + 'stops the copy — import the match again later to resume it. Leave anyway?'

/** While `active`: ask before the tab closes or reloads, and before a
 *  click on a link takes you to another page of the app. (The app's
 *  BrowserRouter has no navigation blocker, so in-app links are caught at
 *  the click, in the capture phase, before the router sees it.) */
export function useLeaveGuard(active: boolean) {
  useEffect(() => {
    if (!active) return
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault()
      e.returnValue = ''   // older browsers need it set to show the dialog
    }
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0
          || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return
      const a = (e.target as Element | null)?.closest?.('a[href]')
      if (!(a instanceof HTMLAnchorElement) || a.target === '_blank') return
      const to = new URL(a.href, window.location.href)
      if (to.origin === window.location.origin
          && to.pathname === window.location.pathname) return
      if (!window.confirm(LEAVE_WARNING)) {
        e.preventDefault()
        e.stopPropagation()
      }
    }
    window.addEventListener('beforeunload', onUnload)
    document.addEventListener('click', onClick, true)
    return () => {
      window.removeEventListener('beforeunload', onUnload)
      document.removeEventListener('click', onClick, true)
    }
  }, [active])
}
